"use client";

import { useEffect, useState } from "react";
import { useAuth } from "@/modules/hooks/auth/useAuth";
import { useGoogleOneTapLogin } from "@react-oauth/google";
import { AppError } from "@/lib/utils/errors";
import { useRouter, usePathname } from "next/navigation";
import { routes } from "@/lib/utils/routes";
import { useSnackbar } from "notistack";
import { apiClient } from "@/lib/api/api-client";
import { employeeService } from "@/modules/services/employeeService";
import { StatusCode } from "@/modules/types/statusCode";
import { Gender } from "@/modules/types/enum";
import { cookieService } from "@/lib/cookie/cookieService";

const OneTapComponent = () => {
  const { login } = useAuth();
  const router = useRouter();
  const { enqueueSnackbar } = useSnackbar();

  useGoogleOneTapLogin({
    onSuccess: async (credentialResponse) => {
      try {
        if (!credentialResponse.credential) return;
        
        // Decode JWT payload manually without external lib
        const base64Url = credentialResponse.credential.split(".")[1];
        const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
        const jsonPayload = decodeURIComponent(
          atob(base64)
            .split("")
            .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
            .join("")
        );
        const userInfo = JSON.parse(jsonPayload);

        const resp = await login(
          userInfo.email,
          "",
          true,
          "google",
          userInfo.sub,
          userInfo.name
        );
        
        if (resp) {
          apiClient.setToken(resp.token);
          try {
            const empResp = await employeeService.getByDefault();
            if (empResp && empResp.code === StatusCode.OK && empResp.employeeInfo) {
              enqueueSnackbar("Đăng nhập thành công!", { variant: "success" });
              router.refresh();
            } else {
              enqueueSnackbar("Vui lòng cập nhật thông tin cá nhân", { variant: "info" });
              router.push(routes.auth.signup);
            }
          } catch(err) {
            const appErr = new AppError(err);
            if (appErr.code === StatusCode.NOT_FOUND) {
              const infoData = {
                firstName: userInfo.given_name || userInfo.name || "",
                lastName: userInfo.family_name || "",
                phone: "",
                birthDay: 0,
                gender: Gender.male,
                slug: (userInfo.name || "").toLowerCase().replace(/[^a-z0-9]/g, "-") + "-" + Date.now(),
                title: "Candidate",
              };
              try {
                await employeeService.registerInfo(infoData);
                const newEmpResp = await employeeService.getByDefault();
                if (newEmpResp && newEmpResp.code === StatusCode.OK && newEmpResp.employeeInfo) {
                  if (userInfo.picture) {
                    await employeeService.updateAvatar(newEmpResp.employeeInfo.id, userInfo.picture);
                  }
                  enqueueSnackbar("Đăng nhập thành công!", { variant: "success" });
                  router.refresh();
                  return;
                }
              } catch (createErr) {
                console.error("Auto create employee failed", createErr);
              }
              enqueueSnackbar("Vui lòng cập nhật thông tin cá nhân", { variant: "info" });
              router.push(routes.auth.signup);
            }
          }
        }
      } catch (error) {
        enqueueSnackbar("Đăng nhập thất bại", { variant: "error" });
        console.error(error);
      }
    },
    onError: () => {
      console.log("One Tap Login Failed or Closed");
    },
  });

  return null;
};

export const GoogleOneTap = () => {
  const { isAuthenticated } = useAuth();
  const [showPrompt, setShowPrompt] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    // Không hiển thị popup ở các trang login/signup
    if (pathname.includes("/auth/")) {
      return;
    }

    // Check cả state và cookie để chắc chắn user chưa đăng nhập
    const cookieToken = cookieService.get("token", { encrypt: true });
    if (isAuthenticated || cookieToken) {
      setShowPrompt(false);
      return;
    }

    // Hiển thị popup sau 30 giây (30000 ms)
    const timer = setTimeout(() => {
      setShowPrompt(true);
    }, 10000);

    return () => clearTimeout(timer);
  }, [isAuthenticated, pathname]);

  if (!showPrompt) return null;

  return <OneTapComponent />;
};
